import React, { CSSProperties } from "react"
import { FormPDF, FormPDFAssetHeader } from "./components/FormPDF"
import { PDFAsset } from "./models/PDFAsset"
import {
    labelForWorkOrderStatus,
    pdfHeaderTitleForWorkOrder,
    PDFWorkOrder,
    PDFWorkOrderStatus,
} from "./models/PDFWorkOrder"
import { PDFWorkRequest, PDFWorkRequestStatus, PDFWorkRequestTask, PDFWorkRequestType } from "./models/PDFWorkRequest"
import { Strings } from "./util/Strings"

/**
 * Action Item 6764: form-style Work Order PDF. Renders a read-only version of the View
 * Work Order form (CreateNewWorkOrderModal) from the Created By / Assigned To line
 * through the last Service Item. Buttons are removed, dropdowns are plain text,
 * attachments are plain file names and Service Item History is not included.
 *
 * The colors and container styles mirror the form's CSS in static/styles/main.scss
 * (.service-item-card, .service-items-container, .task-list-container, form-field
 * variables) so the PDF looks like the rendered form.
 *
 * extendedServiceItems: true renders every Service Item fully extended (Work Order
 * Details print); false renders the collapsed Service Item cards (Work Order Summary
 * print).
 */

// Design tokens lifted from static/styles/main.scss.
const FormColors = {
    primary: "#191e27", // --color-primary (SelectTheme.Dark background)
    inputBackground: "#f1f3f4", // --form-field-background
    cardBorder: "#dddddd", // .service-item-card border
    cardHeaderBackground: "#f9fafb", // .service-item-card .card-header
    readonlyBorder: "#e5e7eb", // .readonly-value / .task-item borders
    labelGray: "#374151", // form labels / totals blocks
    mutedGray: "#6b7280", // .status-block, .subtitle, task descriptions
    darkGray: "#111827", // .status-block strong, task labels
    blue: "#2563eb", // .count pill / .grand-total / checked boxes
    serviceIdBlue: "#0052cc", // .service-id
    totalsBoxBackground: "#f1f5f9", // .totals-box
    measurementBorder: "#d1d5db", // .task-measurement
    measurementBackground: "#f9fafb",
    attachmentBackground: "#fafafa", // .attachment-list
    attachmentBorder: "#cccccc",
    completedGreen: "mediumseagreen", // .completed-banner
    completedGreenBorder: "#059669",
    completedGreenText: "#ffffff",
}

export type WorkOrderFormPDFProps = {
    asset: PDFAsset
    workOrder: PDFWorkOrder
    companyName: string
    companyLogoUrl: string | null
    extendedServiceItems: boolean
}

export function WorkOrderFormPDF(props: WorkOrderFormPDFProps): JSX.Element {
    return (
        <FormPDF pageHeaderTitle={pdfHeaderTitleForWorkOrder(props.workOrder)} companyName={props.companyName}>
            <WorkOrderFormPage {...props} />
        </FormPDF>
    )
}

export function WorkOrderFormPage({ asset, workOrder, companyLogoUrl, extendedServiceItems }: WorkOrderFormPDFProps): JSX.Element {
    const isCompleted =
        workOrder.status === PDFWorkOrderStatus.WorkCompleted || workOrder.status === PDFWorkOrderStatus.Closed

    return (
        <>
            <FormPDFAssetHeader
                title={`${labelForWorkOrderStatus(workOrder.status)} Work Order: ${workOrder.workOrderNumber}`}
                asset={asset}
                workOrder={workOrder}
                companyLogoUrl={companyLogoUrl}
            />
            <PDFFormSection style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 30px" }}>
                {/* Created By / Work Assigned To render as MechanicCards on the form: a label with a bold name, no input box. */}
                <FormPDFField label="Created By">
                    <div style={{ fontWeight: 700, fontSize: "1.4rem", minHeight: "20px" }}>
                        {workOrder.createdByName ?? "Work Order Automation"}
                    </div>
                </FormPDFField>
                <FormPDFField label="Work Assigned To">
                    <div style={{ fontWeight: 700, fontSize: "1.4rem", minHeight: "20px" }}>
                        {workOrder.assignedToName || "Unassigned"}
                    </div>
                </FormPDFField>
            </PDFFormSection>
            <PDFFormSection style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px 30px" }}>
                <FormPDFField label="Work Order Due Date">
                    <FormPDFInputBox>{workOrder.dueDate}</FormPDFInputBox>
                </FormPDFField>
                <FormPDFField label="Due Hour Meter">
                    <FormPDFInputBox>{workOrder.dueHourMeter ?? ""}</FormPDFInputBox>
                </FormPDFField>
                <FormPDFField label="Due Odometer">
                    <FormPDFInputBox>{workOrder.dueOdometer ?? ""}</FormPDFInputBox>
                </FormPDFField>
                <FormPDFField label="Site">
                    <FormPDFInputBox>{workOrder.siteName ?? ""}</FormPDFInputBox>
                </FormPDFField>
            </PDFFormSection>
            {/* Action Item 11894: the completion row is always printed so mechanics can fill it in by hand;
                its values are only shown once the Work Order is completed. */}
            <PDFFormSection style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px 30px" }}>
                {/* Spans two columns with separate date and time boxes, like the legacy print. */}
                <FormPDFField label="Completion Date & Time" style={{ gridColumn: "span 2" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 30px" }}>
                        <FormPDFInputBox>{isCompleted ? workOrder.completionDate : ""}</FormPDFInputBox>
                        <FormPDFInputBox>{isCompleted ? workOrder.completionTime : ""}</FormPDFInputBox>
                    </div>
                </FormPDFField>
                <FormPDFField label="Physical Hour Meter">
                    <FormPDFInputBox>{isCompleted ? workOrder.physicalHourMeter ?? "" : ""}</FormPDFInputBox>
                </FormPDFField>
                <FormPDFField label="Physical Odometer">
                    <FormPDFInputBox>{isCompleted ? workOrder.physicalOdometer ?? "" : ""}</FormPDFInputBox>
                </FormPDFField>
            </PDFFormSection>
            <PDFFormSection>
                <FormPDFField label="Description">
                    <FormPDFInputBox style={{ minHeight: "56px", padding: "8px 10px" }}>
                        {workOrder.specialInstructions}
                    </FormPDFInputBox>
                </FormPDFField>
            </PDFFormSection>
            <ServiceItemsSection workOrder={workOrder} extendedServiceItems={extendedServiceItems} />
        </>
    )
}

function ServiceItemsSection({
    workOrder,
    extendedServiceItems,
}: {
    workOrder: PDFWorkOrder
    extendedServiceItems: boolean
}): JSX.Element {
    const laborTotal = sumCosts(workOrder.workRequests.map((it) => it.actualLaborCost))
    const partsTotal = sumCosts(workOrder.workRequests.map((it) => it.actualPartsCost))
    const miscTotal = sumCosts(workOrder.workRequests.map((it) => it.actualOtherCost))

    return (
        <>
            {/* Mirrors .service-items-header: bold title with the blue count pill on the left, the gray totals box on the right. */}
            <PDFFormSection
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "16px",
                }}
            >
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontWeight: 700, fontSize: "1.6rem" }}>Service Items</span>
                        <span
                            style={{
                                background: FormColors.blue,
                                color: "#ffffff",
                                fontSize: "1.1rem",
                                fontWeight: 600,
                                padding: "1px 8px",
                                borderRadius: "9999px",
                            }}
                        >
                            {workOrder.workRequests.length}
                        </span>
                    </div>
                    <div style={{ color: FormColors.mutedGray, fontSize: "1.3rem", marginTop: "3px" }}>
                        Individual services and tasks for this work order
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        gap: "24px",
                        background: FormColors.totalsBoxBackground,
                        borderRadius: "5px",
                        padding: "6px 14px",
                        fontSize: "1.2rem",
                        color: FormColors.labelGray,
                    }}
                >
                    <ServiceItemsTotal label="Labor" value={laborTotal} />
                    <ServiceItemsTotal label="Parts" value={partsTotal} />
                    <ServiceItemsTotal label="Misc" value={miscTotal} />
                    <ServiceItemsTotal label="Total" value={laborTotal + partsTotal + miscTotal} isGrandTotal={true} />
                </div>
            </PDFFormSection>
            {workOrder.workRequests.map((it, i) =>
                extendedServiceItems ? (
                    <ExtendedServiceItem key={i} workRequest={it} />
                ) : (
                    <CollapsedServiceItem key={i} workRequest={it} />
                )
            )}
        </>
    )
}

function ServiceItemsTotal({
    label,
    value,
    isGrandTotal = false,
}: {
    label: string
    value: number
    isGrandTotal?: boolean
}): JSX.Element {
    return (
        <div>
            <div>{label}</div>
            <div style={{ fontWeight: 700, color: isGrandTotal ? FormColors.blue : FormColors.darkGray }}>
                {"$" + Strings.formatMoney(value)}
            </div>
        </div>
    )
}

// Card container matching .service-item-card: white, 1px #ddd border, 8px radius.
function ServiceItemCard({ children, style }: { children: React.ReactNode; style?: CSSProperties }): JSX.Element {
    return (
        <div
            className="pdf-section"
            style={{
                border: `1px solid ${FormColors.cardBorder}`,
                borderRadius: "8px",
                padding: "8px",
                background: "#ffffff",
                ...style,
            }}
        >
            {children}
        </div>
    )
}

function CollapsedServiceItem({ workRequest }: { workRequest: PDFWorkRequest }): JSX.Element {
    return (
        <ServiceItemCard style={{ breakInside: "avoid" }}>
            <ServiceItemCardHeader workRequest={workRequest} />
            {workRequest.status === PDFWorkRequestStatus.Done && <WorkCompletedBanner />}
        </ServiceItemCard>
    )
}

function ExtendedServiceItem({ workRequest }: { workRequest: PDFWorkRequest }): JSX.Element {
    const workPerformedBy = workRequest.workPerformedBy ?? []
    const isStep = workRequest.stepLabel != null && workRequest.stepLabel !== ""

    return (
        <ServiceItemCard>
            <div style={{ breakInside: "avoid" }}>
                <ServiceItemCardHeader workRequest={workRequest} />
                {workRequest.status === PDFWorkRequestStatus.Done && <WorkCompletedBanner />}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px 30px", padding: "10px 8px 4px 8px" }}>
                <FormPDFField label={isStep ? "Step" : "Service Item Name"} style={{ breakInside: "avoid" }}>
                    {/* The Step dropdown uses SelectTheme.Dark on the form: dark box, white text. */}
                    {isStep ? (
                        <FormPDFInputBox
                            style={{ background: FormColors.primary, color: "#ffffff", fontWeight: 600 }}
                        >
                            {workRequest.stepLabel}
                        </FormPDFInputBox>
                    ) : (
                        <FormPDFInputBox>{workRequest.name}</FormPDFInputBox>
                    )}
                </FormPDFField>
                <FormPDFField label="Work Performed By" style={{ breakInside: "avoid" }}>
                    <FormPDFInputBox>{workPerformedBy.length > 0 ? workPerformedBy.join(", ") : "-"}</FormPDFInputBox>
                </FormPDFField>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 30px", breakInside: "avoid" }}>
                    <FormPDFField label="Service Code">
                        <FormPDFInputBox>{workRequest.serviceCode || "-"}</FormPDFInputBox>
                    </FormPDFField>
                </div>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                        gap: "10px 30px",
                        breakInside: "avoid",
                    }}
                >
                    <FormPDFField label="Actual Labor Hours">
                        <FormPDFInputBox>{laborTimeLabel(workRequest.actualLaborHours ?? null)}</FormPDFInputBox>
                    </FormPDFField>
                    <FormPDFField label="Actual Labor Cost">
                        <FormPDFInputBox>{costLabel(workRequest.actualLaborCost ?? null)}</FormPDFInputBox>
                    </FormPDFField>
                    <FormPDFField label="Actual Parts Cost">
                        <FormPDFInputBox>{costLabel(workRequest.actualPartsCost ?? null)}</FormPDFInputBox>
                    </FormPDFField>
                    <FormPDFField label="Misc Cost">
                        <FormPDFInputBox>{costLabel(workRequest.actualOtherCost ?? null)}</FormPDFInputBox>
                    </FormPDFField>
                </div>
                <FormPDFField label="Mechanics Notes" style={{ breakInside: "avoid" }}>
                    <FormPDFInputBox style={{ minHeight: "40px", padding: "8px 10px" }}>
                        {workRequest.notes?.trim() ? workRequest.notes : ""}
                    </FormPDFInputBox>
                </FormPDFField>
                {(workRequest.tasks?.length ?? 0) > 0 && (
                    <ServiceItemTaskList type={workRequest.type} tasks={workRequest.tasks ?? []} />
                )}
                <FormPDFField label="Attachments" style={{ breakInside: "avoid" }}>
                    {/* Mirrors .attachment-list: dashed border, light background, one row per file. */}
                    <div
                        style={{
                            border: `1px dashed ${FormColors.attachmentBorder}`,
                            borderRadius: "6px",
                            padding: "6px 10px",
                            background: FormColors.attachmentBackground,
                            fontSize: "1.3rem",
                        }}
                    >
                        {(workRequest.attachmentNames?.length ?? 0) > 0 ? (
                            (workRequest.attachmentNames ?? []).map((name, i) => (
                                <div key={i} style={{ padding: "2px 0" }}>
                                    {name}
                                </div>
                            ))
                        ) : (
                            <>None</>
                        )}
                    </div>
                </FormPDFField>
            </div>
        </ServiceItemCard>
    )
}

// Mirrors .card-header.readonly-header: light gray rounded strip, blue SERVICE # id,
// bold name, centered status/due blocks. The actions column is intentionally absent.
function ServiceItemCardHeader({ workRequest }: { workRequest: PDFWorkRequest }): JSX.Element {
    const isCompleted = workRequest.status === PDFWorkRequestStatus.Done

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: "4px 24px",
                alignItems: "center",
                padding: "8px 10px",
                background: FormColors.cardHeaderBackground,
                borderRadius: "6px",
            }}
        >
            <div>
                <div style={{ fontSize: "1.1rem", fontWeight: 600, color: FormColors.serviceIdBlue }}>
                    SERVICE #: {workRequest.id ? workRequest.id.toString().padStart(6, "0") : "PENDING"}
                </div>
                <div style={{ fontWeight: 600, fontSize: "1.4rem", color: FormColors.darkGray }}>
                    {workRequest.name || "Service Item"}
                </div>
            </div>
            {isCompleted ? (
                <div style={{ display: "flex", gap: "20px", fontSize: "1.2rem", textAlign: "center" }}>
                    <ServiceItemHeaderBlock label="Labor Time" value={laborTimeLabel(workRequest.actualLaborHours ?? null)} />
                    <ServiceItemHeaderBlock
                        label="Total Cost"
                        value={
                            "$" +
                            Strings.formatMoney(
                                sumCosts([
                                    workRequest.actualLaborCost,
                                    workRequest.actualPartsCost,
                                    workRequest.actualOtherCost,
                                ])
                            )
                        }
                    />
                </div>
            ) : (
                <div style={{ display: "flex", gap: "20px", fontSize: "1.2rem", textAlign: "center" }}>
                    <ServiceItemHeaderBlock label="Due Hours" value={workRequest.dueHourMeter?.toString() ?? "-"} />
                    <ServiceItemHeaderBlock label="Due Miles" value={workRequest.dueOdometer?.toLocaleString() ?? "-"} />
                    <ServiceItemHeaderBlock label="Due Date" value={workRequest.dueDate ?? "-"} />
                </div>
            )}
        </div>
    )
}

function ServiceItemHeaderBlock({ label, value }: { label: string; value: string }): JSX.Element {
    return (
        <div>
            <div style={{ color: FormColors.mutedGray }}>{label}</div>
            <div style={{ fontWeight: 600, color: FormColors.darkGray }}>{value}</div>
        </div>
    )
}

// Mirrors .completed-banner: green rounded strip with a check and uppercase title.
function WorkCompletedBanner(): JSX.Element {
    return (
        <div
            style={{
                marginTop: "8px",
                padding: "8px 12px",
                borderRadius: "10px",
                background: FormColors.completedGreen,
                border: `1px solid ${FormColors.completedGreenBorder}`,
                color: FormColors.completedGreenText,
                fontWeight: 700,
                fontSize: "1.2rem",
                letterSpacing: "0.03em",
            }}
        >
            &#10003; WORK COMPLETED
        </div>
    )
}

function ServiceItemTaskList({ type, tasks }: { type: PDFWorkRequestType; tasks: PDFWorkRequestTask[] }): JSX.Element {
    const isInspection = type === PDFWorkRequestType.Inspection
    const checkColumns = isInspection ? ["Pass", "Fail", "Not Performed", "N/A"] : ["Completed", "Not Performed", "N/A"]
    // Column widths from .task-list__header.is-inspection / .is-preventative.
    const gridTemplateColumns = isInspection ? "40px 40px 80px 40px 1fr 110px" : "60px 90px 40px 1fr 110px"

    return (
        <FormPDFField label="Tasks to be Performed">
            <div style={{ marginTop: "2px" }}>
                <div style={{ fontSize: "1.3rem", fontWeight: 600, color: FormColors.labelGray, marginBottom: "4px" }}>
                    Check Items
                </div>
                {/* Header row mirrors .task-list__header: small muted labels. */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns,
                        columnGap: "10px",
                        alignItems: "center",
                        textAlign: "center",
                        fontSize: "1.1rem",
                        color: FormColors.mutedGray,
                        padding: "0 5px",
                        marginBottom: "4px",
                        breakInside: "avoid",
                    }}
                >
                    {checkColumns.map((column) => (
                        <div key={column}>{column}</div>
                    ))}
                    <div />
                    <div>Measurement</div>
                </div>
                {[...tasks].sort((a, b) => a.name.localeCompare(b.name)).map((task, i) => (
                    <ServiceItemTask
                        key={i}
                        task={task}
                        checkColumns={checkColumns}
                        gridTemplateColumns={gridTemplateColumns}
                    />
                ))}
            </div>
        </FormPDFField>
    )
}

// Mirrors .task-item: each task is its own white, bordered, rounded row.
function ServiceItemTask({
    task,
    checkColumns,
    gridTemplateColumns,
}: {
    task: PDFWorkRequestTask
    checkColumns: string[]
    gridTemplateColumns: string
}): JSX.Element {
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns,
                columnGap: "10px",
                alignItems: "center",
                border: `1px solid ${FormColors.readonlyBorder}`,
                borderRadius: "8px",
                background: "#ffffff",
                marginBottom: "6px",
                padding: "6px 5px",
                breakInside: "avoid",
            }}
        >
            {checkColumns.map((column) => (
                <FormPDFCheckbox key={column} checked={task.status === column} />
            ))}
            <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "1.3rem", fontWeight: 500, color: FormColors.darkGray }}>{task.name}</div>
                {task.description && (
                    <div style={{ fontSize: "1.1rem", color: FormColors.mutedGray, marginTop: "1px" }}>
                        {task.description}
                    </div>
                )}
            </div>
            {/* Mirrors .task-measurement: bordered, rounded, light input. */}
            <div
                style={{
                    border: `1px solid ${FormColors.measurementBorder}`,
                    borderRadius: "6px",
                    background: FormColors.measurementBackground,
                    minHeight: "18px",
                    padding: "2px 6px",
                    fontSize: "1.2rem",
                    textAlign: "center",
                }}
            >
                {task.measurement ?? ""}
            </div>
        </div>
    )
}

// Blue checkbox like the form's checked state; light bordered square when unchecked.
function FormPDFCheckbox({ checked }: { checked: boolean }): JSX.Element {
    return (
        <div
            style={{
                width: 13,
                height: 13,
                borderRadius: "3px",
                border: `1px solid ${checked ? FormColors.blue : "#9ca3af"}`,
                background: checked ? FormColors.blue : "#ffffff",
                color: "#ffffff",
                fontSize: "10px",
                lineHeight: "12px",
                textAlign: "center",
                justifySelf: "center",
            }}
        >
            {checked ? "✓" : ""}
        </div>
    )
}

function PDFFormSection({ children, style }: { children: React.ReactNode; style?: CSSProperties }): JSX.Element {
    return (
        <div className="pdf-section" style={{ breakInside: "avoid", ...style }}>
            {children}
        </div>
    )
}

export function FormPDFField({
    label,
    children,
    style,
}: {
    label: string
    children?: React.ReactNode
    style?: CSSProperties
}): JSX.Element {
    return (
        <div style={style}>
            <div style={{ fontWeight: 600, fontSize: "1.2rem", color: FormColors.labelGray, marginBottom: 4 }}>
                {label}
            </div>
            <div style={{ fontSize: "1.4rem", lineHeight: "1.25em" }}>{children}</div>
        </div>
    )
}

// Read-only stand-in for the form's inputs/dropdowns: --form-field-background gray,
// 4px radius, same padding scale as --form-field-padding.
export function FormPDFInputBox({ children, style }: { children?: React.ReactNode; style?: CSSProperties }): JSX.Element {
    return (
        <div
            style={{
                background: FormColors.inputBackground,
                borderRadius: "4px",
                padding: "5px 10px",
                minHeight: "24px",
                fontSize: "1.3rem",
                color: FormColors.labelGray,
                ...style,
            }}
        >
            {children}
        </div>
    )
}

function laborTimeLabel(hours: number | null): string {
    if (hours === null) return "-"
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    return `${wholeHours}h ${minutes}m`
}

function costLabel(cost: number | null): string {
    return cost === null ? "-" : "$" + Strings.formatMoney(cost)
}

function sumCosts(costs: (number | null | undefined)[]): number {
    return costs.reduce((acc: number, cost) => acc + (cost ?? 0), 0)
}
